/**
 * Custom Date Picker Component
 * Accessible date picker with custom styling and validation
 */

"use client";

import { useState, useEffect, useId, forwardRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIconLucide,
} from "lucide-react";
import ReactDatePicker from "react-datepicker";
import {
  getMonth,
  getYear,
  isValid,
  parse,
  format,
  isEqual,
  startOfDay,
  isBefore,
  isToday,
  getDate,
} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { cn } from "@/lib/utils";
import { Button } from "./Button";
import { Label } from "./Label";
import { Input } from "./Input";

interface CustomDatePickerProps {
  date: Date | null;
  onDateChange: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  testId?: string;
  disabled?: boolean;
  inputClassName?: string;
  disablePastDates?: boolean;
  maxDate?: Date;
  dateFormat?: string;
  showClearButton?: boolean;
  showOkCancelButtons?: boolean;
}

export function CustomDatePicker({
  date: committedDate,
  onDateChange,
  label,
  placeholder = "Select date",
  testId = "date-input",
  disabled,
  inputClassName,
  disablePastDates = false,
  maxDate,
  dateFormat = "yyyy-MM-dd",
  showClearButton = true,
  showOkCancelButtons = true,
}: CustomDatePickerProps) {
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1900 + 1 },
    (_, i) => currentYear - i
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [pickerDate, setPickerDate] = useState<Date | null>(null);

  const formatDate = (dateToFormat: Date | null): string => {
    return dateToFormat && isValid(dateToFormat)
      ? format(dateToFormat, dateFormat)
      : "";
  };

  // This effect runs when the calendar opens or closes
  useEffect(() => {
    if (isCalendarOpen) {
      const initialPickerDate =
        committedDate && isValid(committedDate) ? committedDate : new Date();
      setPickerDate(initialPickerDate);
      setInputValue(formatDate(initialPickerDate));
    } else {
      // When calendar closes, revert to the last committed state
      setInputValue(formatDate(committedDate));
      setPickerDate(committedDate);
    }
  }, [committedDate, isCalendarOpen, dateFormat]);

  // Sync the input with the picker state while open
  useEffect(() => {
    if (isCalendarOpen) {
      setInputValue(formatDate(pickerDate));
    }
  }, [pickerDate, isCalendarOpen, dateFormat]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (value === "") {
      setPickerDate(null);
      return;
    }

    // Parse using the dateFormat prop - supports any date-fns format
    const parsed = parse(value, dateFormat, new Date());

    if (isValid(parsed)) {
      if (
        disablePastDates &&
        isBefore(parsed, startOfDay(new Date())) &&
        !isEqual(parsed, startOfDay(new Date()))
      ) {
        setPickerDate(null);
        return;
      }
      setPickerDate(parsed);
    } else {
      setPickerDate(null);
    }
  };

  const handleInputBlur = () => {
    const currentPickerFormatted = formatDate(pickerDate);
    if (inputValue !== currentPickerFormatted) {
      if (pickerDate && isValid(pickerDate)) {
        setInputValue(formatDate(pickerDate));
      } else {
        setInputValue(formatDate(committedDate));
        setPickerDate(committedDate);
      }
    }
  };

  const handleCalendarDayClick = (newCalDate: Date | null) => {
    if (newCalDate && isValid(newCalDate)) {
      if (
        disablePastDates &&
        isBefore(newCalDate, startOfDay(new Date())) &&
        !isEqual(newCalDate, startOfDay(new Date()))
      ) {
        return;
      }
      setPickerDate(newCalDate);

      if (!showOkCancelButtons) {
        onDateChange(newCalDate);
        setIsCalendarOpen(false);
      }
    } else if (newCalDate === null) {
      setPickerDate(null);
      if (!showOkCancelButtons) {
        onDateChange(null);
        setIsCalendarOpen(false);
      }
    }
  };

  const handleOK = () => {
    if (
      pickerDate &&
      disablePastDates &&
      isBefore(pickerDate, startOfDay(new Date())) &&
      !isEqual(pickerDate, startOfDay(new Date()))
    ) {
      onDateChange(null);
    } else {
      onDateChange(pickerDate);
    }
    setIsCalendarOpen(false);
  };

  const handleCancel = () => {
    setIsCalendarOpen(false);
  };

  const handleClear = () => {
    setPickerDate(null);
    if (!showOkCancelButtons) {
      onDateChange(null);
      setIsCalendarOpen(false);
    }
  };

  const inputId = useId();

  const CustomInput = forwardRef<
    HTMLInputElement,
    { value?: string; onClick?: () => void }
  >(
    (
      { onClick: onReactDatePickerClick },
      ref
    ) => (
      <div className="ww-relative ww-w-full">
        <Input
          ref={ref}
          id={inputId}
          data-testid={testId}
          aria-label={label || "Date input"}
          className={cn("ww-w-full ww-pr-10", inputClassName)}
          value={inputValue}
          onClick={() => {
            if (onReactDatePickerClick) onReactDatePickerClick();
            setIsCalendarOpen((prev) => !prev);
          }}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
        />
        <CalendarIconLucide
          className="ww-absolute ww-right-3 ww-top-1/2 ww-h-4 ww-w-4 ww--translate-y-1/2 ww-text-gray-400 ww-cursor-pointer"
          onClick={() => !disabled && setIsCalendarOpen((prev) => !prev)}
          aria-hidden="true"
        />
      </div>
    )
  );
  CustomInput.displayName = "CustomDatePickerInput";

  const effectiveShouldCloseOnSelect = !showOkCancelButtons;

  return (
    <div className="ww-w-full">
      {label && (
        <Label
          htmlFor={inputId}
          className="ww-mb-1 ww-block ww-text-xs ww-font-medium ww-text-gray-600"
        >
          {label}
        </Label>
      )}
      <ReactDatePicker
        selected={pickerDate}
        onChange={handleCalendarDayClick}
        shouldCloseOnSelect={effectiveShouldCloseOnSelect}
        minDate={disablePastDates ? startOfDay(new Date()) : undefined}
        maxDate={maxDate}
        open={isCalendarOpen}
        onCalendarOpen={() => setIsCalendarOpen(true)}
        onCalendarClose={() => setIsCalendarOpen(false)}
        onClickOutside={() => setIsCalendarOpen(false)}
        disabled={disabled}
        customInput={<CustomInput />}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        preventOpenOnFocus={true}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="ww-flex ww-items-center ww-justify-between ww-px-4 ww-py-2 ww-border-b ww-border-gray-200">
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              type="button"
              className="ww-p-1 ww-rounded ww-text-gray-600 hover:ww-bg-gray-100 disabled:ww-opacity-50 disabled:ww-cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="ww-flex ww-gap-2">
              <select
                value={months[getMonth(date)]}
                onChange={({ target: { value } }) => {
                  const newMonthIndex = months.indexOf(value);
                  const newDate = new Date(
                    getYear(date),
                    newMonthIndex,
                    getDate(date)
                  );
                  setPickerDate(newDate);
                  changeMonth(newMonthIndex);
                }}
                className="ww-px-2 ww-py-1 ww-text-sm ww-border ww-border-gray-300 ww-rounded ww-bg-white focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20"
              >
                {months.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <select
                value={getYear(date)}
                onChange={({ target: { value } }) => {
                  const newYear = parseInt(value, 10);
                  const newDate = new Date(
                    newYear,
                    getMonth(date),
                    getDate(date)
                  );
                  setPickerDate(newDate);
                  changeYear(newYear);
                }}
                className="ww-px-2 ww-py-1 ww-text-sm ww-border ww-border-gray-300 ww-rounded ww-bg-white focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20"
              >
                {years.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              type="button"
              className="ww-p-1 ww-rounded ww-text-gray-600 hover:ww-bg-gray-100 disabled:ww-opacity-50 disabled:ww-cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
        calendarClassName="ww-bg-white ww-border ww-rounded-md ww-shadow-lg"
        popperClassName="!ww-z-[150]"
        dayClassName={(currentDay: Date): string => {
          const isPast = isBefore(currentDay, startOfDay(new Date()));
          const isPastAndDisabled =
            disablePastDates &&
            isPast &&
            !isEqual(currentDay, startOfDay(new Date()));

          if (isPastAndDisabled)
            return cn(
              "ww-text-gray-400 ww-opacity-50 ww-cursor-not-allowed",
              "ww-px-1 ww-py-1 ww-m-0.5 ww-text-center"
            );

          let classes =
            "ww-text-sm ww-px-1 ww-py-1 ww-m-0.5 ww-text-center ww-rounded-md hover:ww-bg-gray-100 hover:ww-text-gray-900 focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20";

          if (
            pickerDate &&
            isValid(pickerDate) &&
            isEqual(startOfDay(currentDay), startOfDay(pickerDate))
          ) {
            classes = cn(
              classes,
              "ww-bg-primary ww-text-white ww-font-semibold"
            );
          } else if (isToday(currentDay)) {
            classes = cn(
              classes,
              "ww-text-primary ww-font-bold ww-ring-1 ww-ring-primary/50"
            );
          }

          return classes;
        }}
      >
        {showOkCancelButtons && (
          <div className="ww-flex ww-justify-between ww-items-center ww-p-3 ww-border-t ww-bg-white ww-w-full">
            {showClearButton ? (
              <Button
                variant="ghost"
                onClick={handleClear}
                className="ww-text-sm ww-text-red-600 hover:ww-text-red-700 ww-font-medium"
              >
                Clear
              </Button>
            ) : (
              <div />
            )}
            <div className="ww-flex ww-gap-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="ww-text-sm ww-text-gray-700 hover:ww-text-gray-800 ww-font-medium"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleOK}
                className="ww-text-sm ww-px-4 ww-font-medium"
              >
                OK
              </Button>
            </div>
          </div>
        )}
      </ReactDatePicker>
    </div>
  );
}

